import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Barcode, ChevronLeft, ChevronRight, Pencil, Plus, ScanBarcode, Search, Tags, Trash2, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import AuthMessage from '../../components/AuthMessage';
import { useAuth } from '../../context/AuthContext';
import { useProducts } from '../../context/ProductsContext';
import { listEvents, getEventItems } from '../../api/events';
import { listAllProducts, listProducts, lookupByBarcode } from '../../api/inventory';
import { formatCurrency } from '../../data/mockData';
import { formatPrice, formatStock } from '../../utils/soldBy';
import {
  isMergedProduct,
  tombstoneTarget,
} from '../../utils/productMerge';
import ProductFormDrawer from './ProductFormDrawer';
import CategoryDrawer from './CategoryDrawer';
import ReassignCategoryDrawer from './ReassignCategoryDrawer';
import styles from './Catalog.module.css';

const CATEGORY_ALL = 'ALL';
const PAGE_SIZE = 10;

function looksLikeBarcode(value) {
  const code = String(value || '').trim();
  return /^\d{6,18}$/.test(code);
}

function normCat(value) {
  return String(value || '').trim().toLowerCase();
}

function productMatchesCategory(productCategory, selectedId, categoryById) {
  if (selectedId === CATEGORY_ALL) return true;
  const selected = selectedId ? categoryById.get(selectedId) : null;
  const product = normCat(productCategory);
  if (!product) return false;
  if (product === normCat(selectedId)) return true;
  if (selected?.name && product === normCat(selected.name)) return true;
  return false;
}

export default function Catalog() {
  const { user } = useAuth();
  const {
    products, productsLoading, productsError, reloadProducts, mergeProducts,
    addProduct, updateProduct, deleteProduct,
    categories, categoriesLoading, categoriesError, addCategory, updateCategory, deleteCategory,
    applyProductCategoryMoves,
  } = useProducts();
  // Category delete opens the reassign sheet directly (no inline confirm).
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  const [category, setCategory] = useState(CATEGORY_ALL);
  const [eventId, setEventId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [togglingCategoryId, setTogglingCategoryId] = useState(null);
  const [categoryActionError, setCategoryActionError] = useState('');
  const [categoryInUse, setCategoryInUse] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [productActionError, setProductActionError] = useState('');
  const [storeEvents, setStoreEvents] = useState([]);
  const [pendingEditId, setPendingEditId] = useState(null);
  const [barcodeLookingUp, setBarcodeLookingUp] = useState(false);
  const searchRef = useRef(null);
  const pageTopRef = useRef(null);

  const loadStoreEvents = useCallback(async () => {
    if (!user?.idToken) return;
    try {
      const list = await listEvents(user.idToken);
      setStoreEvents(list);
    } catch {
      setStoreEvents([]);
    }
  }, [user?.idToken]);

  useEffect(() => {
    loadStoreEvents();
  }, [loadStoreEvents]);

  useEffect(() => {
    if (!user?.idToken) return undefined;
    let cancelled = false;
    listAllProducts(user.idToken, { incomplete: true, includeInactive: true })
      .then((items) => { if (!cancelled) mergeProducts(items); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.idToken, mergeProducts]);

  useEffect(() => {
    if (!eventId || !user?.idToken) return undefined;
    let cancelled = false;
    getEventItems(eventId, user.idToken)
      .then((itemIds) => {
        if (cancelled) return;
        setStoreEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, itemIds } : e)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eventId, user?.idToken]);

  useEffect(() => {
    const q = searchParams.get('q');
    const editId = searchParams.get('edit');
    if (q == null && !editId) return;
    if (q != null) {
      setQuery(q);
      setPage(1);
    }
    if (editId) setPendingEditId(editId);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const categoryById = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.id, c));
    return map;
  }, [categories]);
  const categoryLabel = (id) => categoryById.get(id)?.name || id;

  useEffect(() => {
    if (!user?.idToken || category === CATEGORY_ALL) return undefined;
    let cancelled = false;
    listAllProducts(user.idToken, { category, includeInactive: true })
      .then((items) => { if (!cancelled) mergeProducts(items); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [category, user?.idToken, mergeProducts]);

  useEffect(() => {
    const q = query.trim();
    if (!user?.idToken || q.length < 2) return undefined;
    let cancelled = false;

    (async () => {
      try {
        if (looksLikeBarcode(q)) {
          setBarcodeLookingUp(true);
          try {
            const data = await lookupByBarcode(q, user.idToken);
            const item = data?.item;
            const currentBarcode = String(item?.barcode || '').trim();
            if (!cancelled && item && currentBarcode === q) mergeProducts([item]);
            return;
          } catch {
            // Fall through to text search if barcode is not in catalog yet.
          } finally {
            if (!cancelled) setBarcodeLookingUp(false);
          }
        }
        const { items } = await listProducts(user.idToken, {
          q,
          limit: 50,
          includeInactive: true,
        });
        if (!cancelled) mergeProducts(items);
      } catch {
        // Local filter still works on whatever is already loaded.
      }
    })();

    return () => {
      cancelled = true;
      setBarcodeLookingUp(false);
    };
  }, [query, user?.idToken, mergeProducts]);

  const visibleCategories = useMemo(
    () => [{ id: CATEGORY_ALL, name: 'All categories' }, ...categories.filter((c) => c.status === 'ACTIVE')],
    [categories],
  );

  const productCounts = useMemo(() => {
    const counts = {};
    products.forEach((p) => { counts[p.category] = (counts[p.category] || 0) + 1; });
    return counts;
  }, [products]);

  const eventItemIds = useMemo(() => {
    if (!eventId) return null;
    return new Set(storeEvents.find((e) => e.id === eventId)?.itemIds || []);
  }, [eventId, storeEvents]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = products.filter((p) => {
      if (isMergedProduct(p)) return false;
      const haystack = `${p.name || ''} ${p.sku || ''} ${p.barcode || ''}`.toLowerCase();
      const matchesQuery = !needle || haystack.includes(needle);
      const matchesCategory = productMatchesCategory(p.category, category, categoryById);
      const matchesEvent = !eventItemIds || eventItemIds.has(p.id);
      return matchesQuery && matchesCategory && matchesEvent;
    });
    return rows.slice().sort((a, b) => {
      // Exact barcode hit from a scan floats to the top.
      if (looksLikeBarcode(query)) {
        const code = query.trim();
        const aExact = String(a.barcode || '').trim() === code ? 1 : 0;
        const bExact = String(b.barcode || '').trim() === code ? 1 : 0;
        if (aExact !== bExact) return bExact - aExact;
      }
      const tb = Date.parse(b.createdAt || b.updatedAt || '') || 0;
      const ta = Date.parse(a.createdAt || a.updatedAt || '') || 0;
      if (tb !== ta) return tb - ta;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [products, query, category, eventItemIds, categoryById]);

  useEffect(() => { setPage(1); }, [query, category, eventId]);

  useEffect(() => {
    if (!pendingEditId) return;
    const existing = products.find((p) => p.id === pendingEditId);
    if (!existing) return;
    if (isMergedProduct(existing)) {
      setPendingEditId(existing.mergedInto && existing.mergedInto !== pendingEditId ? existing.mergedInto : null);
      return;
    }
    setEditing(existing);
    setDrawerOpen(true);
    setPendingEditId(null);
  }, [pendingEditId, products]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);

  const goToPage = (nextPage) => {
    setPage(nextPage);
    // After page content updates, jump back to the top of the products screen.
    requestAnimationFrame(() => {
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const openAdd = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (p) => {
    if (isMergedProduct(p)) {
      if (p.mergedInto) handleOpenExisting(p.mergedInto);
      return;
    }
    setEditing(p);
    setDrawerOpen(true);
  };
  const handleOpenExisting = (itemId) => {
    const existing = products.find((p) => p.id === itemId);
    if (existing && isMergedProduct(existing)) {
      if (existing.mergedInto && existing.mergedInto !== itemId) handleOpenExisting(existing.mergedInto);
      return;
    }
    if (existing) {
      setEditing(existing);
      setDrawerOpen(true);
      return;
    }
    setPendingEditId(itemId);
    reloadProducts();
  };
  const handleSave = (data) => {
    if (editing) return updateProduct(editing.id, data);
    return addProduct(data);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setProductActionError('');
    try {
      await deleteProduct(id);
      setConfirmId(null);
    } catch (err) {
      const into = tombstoneTarget(err);
      setConfirmId(null);
      if (into) {
        handleOpenExisting(into);
        return;
      }
      setProductActionError(err.detail || err.message || 'Could not delete this product.');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleCategoryStatus = async (id) => {
    const cat = categories.find((c) => c.id === id);
    setTogglingCategoryId(id);
    setCategoryActionError('');
    try {
      await updateCategory(id, { name: cat.name, status: cat.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' });
      return true;
    } catch (err) {
      setCategoryActionError(err.message || 'Could not update this category.');
      return false;
    } finally {
      setTogglingCategoryId(null);
    }
  };

  const handleDeleteAfterReassign = async (id) => {
    await deleteCategory(id);
    if (category === id) setCategory(CATEGORY_ALL);
    setCategoryInUse(null);
    setReassignOpen(false);
  };

  const openReassignFor = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    setCategoryInUse({
      id: categoryId,
      productCount: categoryInUse?.id === categoryId
        ? categoryInUse.productCount
        : (productCounts[categoryId] || 0),
      name: cat?.name || categoryId,
    });
    setCategoryActionError('');
    setReassignOpen(true);
  };

  return (
    <div ref={pageTopRef}>
      <PageHeader
        eyebrow="Catalog"
        title="Products"
        actions={(
          <div className={styles.headerActions}>
            <Button
              variant="outline"
              icon={Tags}
              onClick={() => setCategoryDrawerOpen(true)}
            >
              Categories
            </Button>
            <Button icon={Plus} onClick={openAdd}>
              Add
            </Button>
          </div>
        )}
      />

      <div className={styles.toolbar}>
        <div className={`${styles.searchBox} ${barcodeLookingUp ? styles.searchBoxBusy : ''}`}>
          <ScanBarcode size={16} className={styles.searchScanIcon} aria-hidden />
          <input
            ref={searchRef}
            placeholder="Scan barcode, or search by name / SKU…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              // Hardware scanners typically end with Enter — keep focus ready for the next scan.
              if (e.key === 'Enter') e.preventDefault();
            }}
            autoComplete="off"
            inputMode="search"
            aria-label="Search products by barcode, name, or SKU"
          />
          {query && (
            <button
              type="button"
              className={styles.clearSearch}
              onClick={() => {
                setQuery('');
                searchRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className={styles.chipRow}>
          {visibleCategories.map((c) => (
            <button
              key={c.id}
              className={`${styles.chip} ${category === c.id ? styles.chipActive : ''}`}
              onClick={() => setCategory(c.id)}
            >
              {c.id !== CATEGORY_ALL && (
                c.imageUrl
                  ? <img src={c.imageUrl} alt="" className={styles.chipImg} />
                  : <span className={styles.chipEmoji}>{c.emoji}</span>
              )}
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {storeEvents.length > 0 && (
        <div className={styles.eventFilter}>
          <span className={styles.eventFilterLabel}>Events</span>
          <div className={styles.chipRow}>
            <button
              type="button"
              className={`${styles.chip} ${!eventId ? styles.chipActive : ''}`}
              onClick={() => setEventId(null)}
            >
              All
            </button>
            {storeEvents.map((e) => (
              <button
                key={e.id}
                type="button"
                className={`${styles.chip} ${eventId === e.id ? styles.chipActive : ''}`}
                onClick={() => setEventId(eventId === e.id ? null : e.id)}
              >
                {e.imageUrl && <img src={e.imageUrl} alt="" className={styles.chipImg} />}
                {e.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <AuthMessage>{productActionError}</AuthMessage>

      {productsLoading ? (
        <p className={styles.categoryTag}>Loading products…</p>
      ) : productsError ? (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load products"
          description={productsError}
          action={<Button variant="outline" onClick={reloadProducts}>Retry</Button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No products match"
          description={eventId
            ? 'This event list is empty, or its products are not in the current catalog. Attach items from Festival events — aisle category stays the same.'
            : 'Try a different name, SKU, barcode, or category.'}
        />
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Barcode</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {paged.map((p) => {
                  const low = p.stock <= p.lowStockThreshold;
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className={styles.productCell}>
                          <span className={styles.swatch} style={{ background: `${p.swatch}22`, color: p.swatch }}>
                            {p.images?.[0]?.url ? <img src={p.images[0].url} alt="" className={styles.swatchImg} /> : p.emoji}
                          </span>
                          <div className={styles.productMeta}>
                            <p className={styles.productName}>
                              {p.name}
                              {p.catalogComplete === false && (
                                <span className={styles.needsDetailsBadge}>Needs details</span>
                              )}
                            </p>
                            <p className={styles.productSku}>{p.sku}</p>
                          </div>
                        </div>
                      </td>
                      <td><span className={styles.categoryTag}>{categoryLabel(p.category)}</span></td>
                      <td>
                        <span className={styles.barcode}><Barcode size={12} /> {p.barcode || '—'}</span>
                      </td>
                      <td>
                        <span className={styles.priceCell}>
                          <span className={styles.price}>{formatPrice(p, formatCurrency)}</span>
                          {p.mrp > p.price && <span className={styles.mrp}>{formatCurrency(p.mrp)}</span>}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.stockPill} ${low ? styles.stockLow : ''}`}>
                          {low && <AlertTriangle size={11} />}
                          {formatStock(p.stock, p)}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.statusChip} ${p.status === 'ACTIVE' ? styles.active : styles.draft}`}>
                          {p.status === 'ACTIVE' ? 'Active' : p.status === 'INACTIVE' ? 'Inactive' : 'Draft'}
                        </span>
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          {p.status !== 'INACTIVE' && confirmId === p.id ? (
                            <>
                              <button className={styles.confirmDel} onClick={() => handleDelete(p.id)} disabled={deletingId === p.id}>
                                {deletingId === p.id ? 'Deleting…' : 'Delete'}
                              </button>
                              <button className={styles.iconBtn} onClick={() => setConfirmId(null)} disabled={deletingId === p.id}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className={styles.iconBtn} onClick={() => openEdit(p)} aria-label="Edit"><Pencil size={14} /></button>
                              {p.status !== 'INACTIVE' && (
                                <button className={styles.iconBtn} onClick={() => setConfirmId(p.id)} aria-label="Delete"><Trash2 size={14} /></button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className={styles.paginationBar}>
              <span className={styles.paginationSummary}>
                Showing {rangeStart}–{rangeEnd} of {filtered.length} product{filtered.length === 1 ? '' : 's'}
              </span>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => goToPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className={styles.paginationPage}>Page {currentPage} of {totalPages}</span>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>

          <div className={styles.mobileList} aria-label="Products">
            {paged.map((p) => {
              const low = p.stock <= p.lowStockThreshold;
              const statusLabel = p.status === 'ACTIVE' ? 'Active' : p.status === 'INACTIVE' ? 'Inactive' : 'Draft';
              const confirming = confirmId === p.id;
              return (
                <article key={p.id} className={styles.mobileCard}>
                  <div className={styles.mobileCardTop}>
                    <span className={styles.mobileThumb} style={{ background: `${p.swatch}22`, color: p.swatch }}>
                      {p.images?.[0]?.url
                        ? <img src={p.images[0].url} alt="" className={styles.swatchImg} />
                        : p.emoji}
                    </span>
                    <div className={styles.mobileBody}>
                      <div className={styles.mobileTop}>
                        <h3 className={styles.mobileName}>{p.name}</h3>
                        <span className={`${styles.statusDot} ${p.status === 'ACTIVE' ? styles.statusDotActive : ''}`} title={statusLabel} />
                      </div>
                      <p className={styles.mobileMeta}>
                        {categoryLabel(p.category)}
                        {p.sku ? ` · ${p.sku}` : ''}
                      </p>
                      {p.catalogComplete === false && (
                        <span className={styles.needsDetailsBadge}>Needs details</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.mobileCardFooter}>
                    <div className={styles.mobileStats}>
                      <span className={styles.mobilePrice}>
                        {formatPrice(p, formatCurrency)}
                        {p.mrp > p.price && <span className={styles.mrpInline}>{formatCurrency(p.mrp)}</span>}
                      </span>
                      <span className={`${styles.stockPill} ${low ? styles.stockLow : ''}`}>
                        {low && <AlertTriangle size={11} />}
                        {formatStock(p.stock, p)}
                      </span>
                    </div>

                    {p.status !== 'INACTIVE' && confirming ? (
                      <div className={styles.mobileConfirmRow}>
                        <button
                          type="button"
                          className={styles.confirmDel}
                          onClick={() => handleDelete(p.id)}
                          disabled={deletingId === p.id}
                        >
                          {deletingId === p.id ? 'Deleting…' : 'Delete'}
                        </button>
                        <button
                          type="button"
                          className={styles.mobileCancelBtn}
                          onClick={() => setConfirmId(null)}
                          disabled={deletingId === p.id}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className={styles.mobileActions}>
                        <button
                          type="button"
                          className={styles.mobileActionBtn}
                          onClick={() => openEdit(p)}
                        >
                          <Pencil size={15} />
                          Edit
                        </button>
                        {p.status !== 'INACTIVE' && (
                          <button
                            type="button"
                            className={`${styles.mobileActionBtn} ${styles.mobileActionDanger}`}
                            onClick={() => setConfirmId(p.id)}
                          >
                            <Trash2 size={15} />
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}

            <div className={styles.mobilePagination}>
              <span className={styles.paginationSummary}>
                {rangeStart}–{rangeEnd} of {filtered.length}
              </span>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => goToPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className={styles.paginationPage}>{currentPage} / {totalPages}</span>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <ProductFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        product={editing}
        onOpenExisting={handleOpenExisting}
      />

      <CategoryDrawer
        open={categoryDrawerOpen}
        onClose={() => {
          setCategoryDrawerOpen(false);
        }}
        categories={categories}
        categoriesLoading={categoriesLoading}
        categoriesError={categoriesError}
        productCounts={productCounts}
        onAdd={addCategory}
        onUpdate={updateCategory}
        onToggleStatus={toggleCategoryStatus}
        onOpenReassign={openReassignFor}
        togglingCategoryId={togglingCategoryId}
        actionError={categoryActionError}
        inUseConflict={categoryInUse}
        onClearInUseConflict={() => setCategoryInUse(null)}
      />

      <ReassignCategoryDrawer
        open={reassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        sourceCategory={categoryInUse ? { id: categoryInUse.id, name: categoryInUse.name } : null}
        productCountHint={categoryInUse?.productCount}
        categories={categories}
        onDeleted={handleDeleteAfterReassign}
        onProductsMoved={applyProductCategoryMoves}
      />
    </div>
  );
}
